import { useState } from "react"
import type { FC } from "react"
import type { Post } from "../../models/posts"
import { useDeletePost, useModeratePost } from "../../hooks/usePosts"
import Comments from "./Comments"
import EditPostForm from "./EditPostForm"
import NewPostForm from "./NewPostForm"
import ImageSlider from "./ImageSlider"

interface PostViewProps {
  post: Post
  refresh: () => void
  managePosts?: boolean
}

const PostView: FC<PostViewProps> = ({ post, refresh, managePosts = false }) => {
  const [showEditForm, setShowEditForm] = useState(false)
  const [showRepostForm, setShowRepostForm] = useState(false)
  const [showImageSlider, setShowImageSlider] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [avatarError, setAvatarError] = useState(false)

  const deleteMutation = useDeletePost()
  const moderateMutation = useModeratePost()

  const handleDelete = () => {
    if (window.confirm("Tem a certeza que pretende eliminar o post?")) {
      deleteMutation.mutate(post.id)
    }
  }

  const handleModerate = () => {
    moderateMutation.mutate(post.id)
  }

  const saveToClipboard = () => {
    navigator.clipboard.writeText(post.link)
  }

  const statusClass = post.status === 5 ? "border-l-4 border-yellow-400" : ""

  return (
    <div className={`bg-white shadow rounded-lg mb-4 ${statusClass}`}>
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            {avatarError || !post.user.avatar ? (
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-100 text-blue-600 text-sm font-bold shrink-0">
                {post.user.name?.charAt(0)?.toUpperCase() || "?"}
              </div>
            ) : (
              <img
                src={post.user.avatar}
                alt={post.user.name}
                className="w-10 h-10 rounded-full object-cover"
                onError={() => setAvatarError(true)}
              />
            )}
            <div>
              <h3 className="font-medium text-gray-900 text-sm">{post.user.name}</h3>
              <span className="text-xs text-gray-400">
                <i className="fa fa-clock-o mr-1" />
                {post.created_at}
              </span>
            </div>
          </div>
          <div className="relative">
            <button
              className="btn-options text-gray-400 hover:text-gray-600 p-1"
              onClick={() => setShowMenu(!showMenu)}
            >
              <i className="fa fa-ellipsis-h" />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-8 bg-white shadow-lg rounded border z-10 min-w-[140px]">
                <ul className="py-1 text-sm">
                  <li>
                    <button className="w-full text-left px-4 py-2 hover:bg-gray-100" onClick={saveToClipboard}>
                      <i className="fa fa-link mr-2" /> Copiar link
                    </button>
                  </li>
                  {post.can_edit && (
                    <li>
                      <button className="w-full text-left px-4 py-2 hover:bg-gray-100" onClick={() => setShowEditForm(true)}>
                        <i className="fa fa-pencil mr-2" /> Editar
                      </button>
                    </li>
                  )}
                  {managePosts && (
                    <li>
                      <button className="w-full text-left px-4 py-2 hover:bg-gray-100" onClick={() => setShowRepostForm(true)}>
                        <i className="fa fa-retweet mr-2" /> Republicar
                      </button>
                    </li>
                  )}
                  {post.can_edit && (
                    <li>
                      <button className="w-full text-left px-4 py-2 hover:bg-gray-100 text-red-600" onClick={handleDelete}>
                        <i className="fa fa-trash mr-2" /> Eliminar
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>

        {post.can_moderate && (
          <div className="mb-3">
            <button
              className="bg-green-500 text-white px-3 py-1 rounded text-xs hover:bg-green-600"
              onClick={handleModerate}
            >
              <i className="fa fa-check mr-1" /> Aprovar
            </button>
          </div>
        )}

        <div className="post-description">
          <div className="text-sm text-gray-800 mb-2" dangerouslySetInnerHTML={{ __html: post.content }} />

          {post.content_type === 2 && post.preview_url && (
            <a
              href={post.preview_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block border rounded overflow-hidden mb-3 hover:shadow-md transition"
            >
              {post.preview_img && (
                <img src={post.preview_img} alt={post.preview_title || ""} className="w-full h-auto" />
              )}
              <div className="p-2 text-sm font-medium text-gray-700 bg-gray-50">
                {post.preview_title}
              </div>
            </a>
          )}

          {post.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {post.tags.map((tag) => (
                <span key={tag.id} className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                  {tag.name}
                </span>
              ))}
            </div>
          )}

          {post.content_type === 3 && post.images.length > 0 && (
            <div role="button" tabIndex={0} className="mb-2 cursor-pointer" onClick={() => setShowImageSlider(true)}>
              <img src={post.images[0]} alt="" className="w-full rounded object-cover max-h-96" />
              {post.images.length > 1 && (
                <div className="text-center text-xs text-gray-500 mt-1">
                  {post.images.length} Fotos
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Comments
        postId={post.id}
        userLike={post.user_like}
        likes={post.likes}
        comments={post.comments}
        commentsLength={post.comments_length}
      />

      {showEditForm && (
        <EditPostForm
          post={post}
          onClose={() => setShowEditForm(false)}
          onSaved={refresh}
        />
      )}

      {showRepostForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium">Republicar Post</h3>
              <button className="text-gray-400 hover:text-gray-600" onClick={() => setShowRepostForm(false)}>
                <i className="fa fa-times" />
              </button>
            </div>
            <NewPostForm
              type={post.visible_to ?? 0}
              refresh={refresh}
              repost={1}
              repostId={post.id}
            />
          </div>
        </div>
      )}

      {showImageSlider && (
        <ImageSlider images={post.images} onClose={() => setShowImageSlider(false)} />
      )}
    </div>
  )
}

export default PostView